import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AuthClientEvent } from '@react-keycloak/core/lib/types';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { AppFavicon } from 'app/components/AppFavicon';
import { AppInit } from 'app/components/AppInit';
import { GlobalStyles } from 'app/components/GlobalStyles';
import { ActivityDetectorProvider, WindowFocusDetectorProvider } from 'app/providers';
import { AppCountersProvider } from 'app/providers/AppCountersProvider';
import { SocketContext } from 'app/providers/SocketProvider';
import { initSentry } from 'app/sentry';
import { DEFAULT_THEME } from 'app/theme';
import keycloak from 'keycloak';
import { ActiveMatchProvider } from 'modules/bracket/providers';
import { ChatSessionProvider } from 'modules/chat/providers';
import { RampWidgetProvider } from 'modules/ramp';
import React, { FC, useCallback, useContext, useEffect } from 'react';
import { CombineProviders, ProviderModel } from 'shared/components/CombineProviders';
import { LoadingMessage } from 'shared/HOC/withSplashScreen';

export const AppConfiguration: FC = ({ children }) => {
  const { create, connect, disconnect } = useContext(SocketContext);

  const eventLogger = useCallback(
    (event?: AuthClientEvent) => {
      switch (event) {
        case 'onAuthSuccess': {
          return create(keycloak?.token);
        }
        case 'onAuthRefreshSuccess': {
          disconnect();
          return connect(keycloak.token);
        }
        case 'onAuthError':
        case 'onAuthRefreshError': {
          keycloak.logout();
          window.localStorage.clear();
          return disconnect();
        }
        case 'onAuthLogout': {
          return disconnect();
        }
      }
    },
    [connect, create, disconnect]
  );

  const providers = [
    new ProviderModel(ReactKeycloakProvider, {
      authClient: keycloak,
      onEvent: eventLogger,
      LoadingComponent: <LoadingMessage />,
      initOptions: {
        onLoad: 'check-sso',
        checkLoginIframe: true,
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      },
    }),
    new ProviderModel(AppCountersProvider),
    new ProviderModel(AppInit),
    new ProviderModel(StyledEngineProvider, { injectFirst: true }),
    new ProviderModel(ThemeProvider, { theme: createTheme(DEFAULT_THEME) }),
    new ProviderModel(WindowFocusDetectorProvider),
    new ProviderModel(ActivityDetectorProvider),
    new ProviderModel(ActiveMatchProvider),
    new ProviderModel(ChatSessionProvider),
    new ProviderModel(RampWidgetProvider),
  ];

  useEffect(() => {
    initSentry();
  }, []);

  return (
    <CombineProviders providers={providers}>
      <CssBaseline />
      <GlobalStyles />
      <AppFavicon />
      {children}
    </CombineProviders>
  );
};
